import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledTextField, ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    BimContractObjectCreateInput,
    BimContractObjectSeasonDistributionInput,
    BimWorkOrderObjectSelectionData,
    BimContractObjectUpdateInput,
    BimContractSeasonData,
} from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { cnMixSpace, IconAlert, Informer, Text } from 'uikit';
import { sortByYear } from 'utils';
import { requiredBaseRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { replacerCost } from 'utils/replacerCost';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useBimUniqueContractObject } from '../../Contract/BimContractStructure/hooks/useBimUniqueContractObject';
import { useBimCreateWorkOrderObjectChange } from '../../WorkOrderChange/bimWorkOrderChangeStructure/BimWorkOrderObjectChangeEdit/useBimCreateWorkOrderObjectChange';
import { useBimUpdateWorkOrderObjectChange } from '../../WorkOrderChange/bimWorkOrderChangeStructure/BimWorkOrderObjectChangeEdit/useBimUpdateWorkOrderObjectChange';

import { useBimCreateWorkOrderObject } from './hooks/useBimCreateWorkOrderObject';
import { useBimUpdateWorkOrderObject } from './hooks/useBimUpdateWorkOrderObject';

import Style from './BimWorkOrderObjectEdit.module.css';

export interface IBimWorkOrderObjectEdit {
    contractId?: number;
    workOrderId?: number;
    workOrderChangeId?: number;
    constructionTypeId: number;
    seasons: (BimContractSeasonData | null)[] | null | undefined;
    onClose: (isSuccess?: boolean) => void;
    objectToEdit?: BimWorkOrderObjectSelectionData;
    isNameUnconditionallyEnabled?: boolean;
}

export const BimWorkOrderObjectEdit: React.FC<IBimWorkOrderObjectEdit> = (props) => {
    const {
        workOrderId,
        contractId,
        constructionTypeId,
        onClose,
        seasons,
        objectToEdit,
        workOrderChangeId,
        isNameUnconditionallyEnabled,
    } = props;
    const [isErrorSubmitted, setIsErrorSubmitted] = useState<boolean>(false);

    const { control, formState, handleSubmit, watch, reset, setError, clearErrors, setValue } = useForm({
        defaultValues: objectToEdit && ({ name: objectToEdit?.name, rationale: objectToEdit?.rationale } as any),
    });

    const watchAllFields = watch();
    const isDirtyObjectName = formState?.dirtyFields?.name;

    const btnConfirmTitle = useMemo(() => `${objectToEdit ? 'Редактирование' : 'Создание'} объекта`, [objectToEdit]);
    const btnConfirmLabel = objectToEdit ? 'Сохранить' : 'Создать';

    const onSuccess = useCallback(() => {
        onClose(true);
    }, [onClose]);

    const { bimCreateWorkOrderObjectMakeRequest } = useBimCreateWorkOrderObject(workOrderId, onSuccess);

    const { bimUpdateWorkOrderObjectMakeRequest } = useBimUpdateWorkOrderObject(workOrderId, onSuccess);

    const { bimCreateWorkOrderObjectChangeMakeRequest } = useBimCreateWorkOrderObjectChange(
        workOrderChangeId,
        onSuccess,
    );

    const {
        bimUpdateWorkOrderChangeObjectData,
        bimUpdateWorkOrderChangeObjectStatus,
        bimUpdateWorkOrderChangeObjectError,
        bimUpdateWorkOrderObjectChangeMakeRequest,
    } = useBimUpdateWorkOrderObjectChange(workOrderChangeId, onSuccess);

    const { bimUniqueContractObjectData, bimUniqueContractObjectStatus, resetBimIsUniqueContractObjectName } =
        useBimUniqueContractObject({
            contractId,
            constructionTypeId,
            name: watchAllFields.name,
            isDirtyObjectName,
        });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const { isOneHundredPercent, isNameFilled, isRationaleFilled } = useMemo(() => {
        const sumPercent = Object.entries(watchAllFields).reduce((acc: number, [fieldName, value]) => {
            if (value && fieldName.includes('season_')) {
                acc += replacerCost(value as string);
            }

            return acc;
        }, 0);

        return {
            isOneHundredPercent: sumPercent === 100,
            isNameFilled: !!watchAllFields.name,
            isRationaleFilled: !!watchAllFields.rationale,
        };
    }, [watchAllFields]);

    const btnConfirmDisabled = useMemo(
        () =>
            !formState?.isDirty ||
            !isOneHundredPercent ||
            !isNameFilled ||
            (isDirtyObjectName && !bimUniqueContractObjectData?.result) ||
            !isRationaleFilled,
        [
            bimUniqueContractObjectData?.result,
            formState?.isDirty,
            isDirtyObjectName,
            isNameFilled,
            isOneHundredPercent,
            isRationaleFilled,
        ],
    );

    const onSubmitDialog = useCallback(
        (formData) => {
            const objectSeasonDistributions: BimContractObjectSeasonDistributionInput[] =
                seasons?.map((s) => ({
                    contractSeasonId: s?.id,
                    seasonValue: replacerCost(formData[`season_${s?.year}`]),
                })) || [];

            if (objectToEdit) {
                const input: BimContractObjectUpdateInput = {
                    hasGuaranteedVolume: true,
                    id: objectToEdit.id,
                    name: formData.name,
                    rationale: formData.rationale,
                    objectSeasonDistributions,
                };

                if (workOrderId) {
                    bimUpdateWorkOrderObjectMakeRequest(input);
                }

                if (workOrderChangeId) {
                    bimUpdateWorkOrderObjectChangeMakeRequest(input);
                    setIsErrorSubmitted(true);
                }
            } else {
                const input: BimContractObjectCreateInput = {
                    objectSeasonDistributions,
                    constructionTypeId,
                    contractId,
                    name: formData.name,
                    rationale: formData.rationale,
                    hasGuaranteedVolume: true,
                };

                if (workOrderId) {
                    bimCreateWorkOrderObjectMakeRequest(input);
                }

                if (workOrderChangeId) {
                    bimCreateWorkOrderObjectChangeMakeRequest(input);
                }
            }
        },
        [
            bimCreateWorkOrderObjectChangeMakeRequest,
            bimCreateWorkOrderObjectMakeRequest,
            bimUpdateWorkOrderObjectChangeMakeRequest,
            bimUpdateWorkOrderObjectMakeRequest,
            constructionTypeId,
            contractId,
            objectToEdit,
            seasons,
            workOrderChangeId,
            workOrderId,
        ],
    );

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const setSeasonsErrors = useCallback(() => {
        seasons?.forEach((season) => setError(`season_${season?.year}`, { message: undefined }));
    }, [seasons, setError]);

    const clearSeasonsErrors = useCallback(() => {
        seasons?.forEach((season) => clearErrors(`season_${season?.year}`));
    }, [clearErrors, seasons]);

    useEffect(() => {
        if (formState?.isDirty && !isOneHundredPercent) {
            setSeasonsErrors();
        } else {
            clearSeasonsErrors();
        }
    }, [clearSeasonsErrors, formState?.isDirty, isOneHundredPercent, setSeasonsErrors]);

    useEffect(() => {
        if (
            watchAllFields.name &&
            bimUniqueContractObjectStatus === RequestStatus.SUCCESS &&
            !bimUniqueContractObjectData?.result
        ) {
            setError('name', { message: 'Объект с таким наименованием уже существует' });
        } else {
            clearErrors('name');
        }
    }, [
        bimUniqueContractObjectData?.result,
        bimUniqueContractObjectStatus,
        clearErrors,
        setError,
        watchAllFields.name,
    ]);

    useEffect(() => {
        const seasonsValue = seasons?.reduce((acc: Record<string, string>, season) => {
            const value = objectToEdit?.objectSeasonDistributions?.find((d) => d.contractSeason.year === season?.year)
                ?.seasonValue;

            acc = { ...acc, [`season_${season?.year}`]: value || '0,00' };

            return acc;
        }, {});

        reset(seasonsValue);

        if (!objectToEdit) {
            setSeasonsErrors();
        }

        const subscription = watch((data, { name }) => {
            if (name?.includes('season') && !data[name]) {
                setValue(name, '0,00');
            }

            return data;
        });

        return () => subscription.unsubscribe();
    }, [objectToEdit, reset, seasons, setSeasonsErrors, setValue, watch]);

    useEffect(
        () => () => {
            resetBimIsUniqueContractObjectName(undefined);
        },
        [resetBimIsUniqueContractObjectName],
    );

    const onCloseModalError = useCallback(() => {
        setIsErrorSubmitted(false);
    }, []);

    const onCreateReport = () => {
        ExportCSVReport(bimUpdateWorkOrderChangeObjectData?.bimUpdateWorkOrderChangeObject.validation);
    };

    return (
        <ModalDialog
            title={btnConfirmTitle}
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Style.modal}
            isOnClickOutsideDisable
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                <Informer
                    view="filled"
                    status="alert"
                    label="Распределение итогов суммарно должно быть 100%"
                    className={cnMixSpace({ mB: 'm' })}
                    icon={IconAlert}
                    size="s"
                />
                <form>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        placeholder="Наименование объекта"
                        control={control}
                        type="text"
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        required
                        rules={requiredBaseRule}
                        disabled={!isNameUnconditionallyEnabled && !!objectToEdit && !objectToEdit?.fromWorkOrder}
                    />
                    <div className={Style.wrapperPercentage}>
                        <Text view="secondary" className={cn(Style.titlePercentage, cnMixSpace({ pB: 'm' }))}>
                            Распределение итогов по сезонам
                            <span className="FieldLabel-Star">*</span>
                        </Text>
                        {[...(seasons ?? [])].sort(sortByYear).map((season, i) => (
                            <ControlNumberField
                                key={`season_${season?.year}`}
                                name={`season_${season?.year}`}
                                label={season?.year}
                                control={control}
                                labelPosition="left"
                                size="s"
                                width="full"
                                rightSide="%"
                                className={cn({ [cnMixSpace({ mT: 'xs' })]: i !== 0 })}
                                rules={requiredBaseRule}
                            />
                        ))}
                    </div>
                    <ControlledTextField
                        className={Style.rationale}
                        labelPosition="top"
                        name="rationale"
                        label="Обоснование"
                        control={control}
                        type="textarea"
                        cols={50}
                        rows={5}
                        required
                        rules={requiredBaseRule}
                    />
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            <ErrorHandlingModal
                titleText="Редактирование объекта"
                isSubmitted={isErrorSubmitted}
                statusRequest={bimUpdateWorkOrderChangeObjectStatus}
                dataRequest={bimUpdateWorkOrderChangeObjectData?.bimUpdateWorkOrderChangeObject}
                errorsRequest={bimUpdateWorkOrderChangeObjectError}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </ModalDialog>
    );
};

BimWorkOrderObjectEdit.displayName = 'BimWorkOrderObjectEdit';
